/** Filing lifecycle for the Batch Filing Review screen.
 *
 *  The batch grid used to know only about PRE-filing completeness ("does every required field have a
 *  value?"), so a policy that had already been filed stayed under "Ready to File" forever. The backend
 *  now stamps each batch-review row with where it actually is in the filing lifecycle; this module is
 *  the single place that reads those fields.
 *
 *  DEFENSIVE BY DESIGN: every accessor tolerates the lifecycle fields being absent (an environment
 *  running an older internal-api) and falls back to the old completeness-only verdict, so the screen
 *  degrades to its previous behaviour instead of breaking.
 */

/** Where a policy is in the filing lifecycle. `notReady` / `ready` are pre-filing completeness;
 *  everything after that describes a submission that has already left the building. */
export type RowFilingStatus = 'notReady' | 'ready' | 'queued' | 'filed' | 'rejected' | 'error' | 'manuallyFiled';

/** The minimum a row must carry for the lifecycle helpers — kept structural (rather than importing
 *  BatchReviewRow) so this module has no dependency on the page that renders it. */
export interface FilingLifecycleRow {
    /** Backend completeness verdict — the pre-lifecycle field, still the fallback. */
    ready?: boolean;
    /** Backend lifecycle stamp. Absent on older API builds. */
    filingStatus?: string | null;
    /** Clearinghouse confirmation number, once a submission exists. */
    submissionNumber?: string | number | null;
    /** ISO timestamp of when the submission was sent. */
    submittedAt?: string | null;
    /** The BATCH DATE: 'YYYY-MM-DD' for the day this policy was marked Ready to File. Computed by the
     *  API from the recorded status transition, in the FILING zone (Central — the nightly run's zone,
     *  deliberately not the product's Mountain display convention), so the frontend never re-derives a
     *  calendar day from an instant. That conversion is where a UTC-vs-local off-by-one creeps in, and
     *  doing it once on the server keeps the picker, the filter and the column agreeing. Absent on
     *  older API builds; null when the policy has no recorded Ready-to-File history. */
    batchDate?: string | null;
    /** When somebody set this policy's status to Filed in this system ('YYYY-MM-DD').
     *  ⚠️ NOT a batch date, and never rendered in the Batch Date column — see batchDateLabel. */
    markedFiledOn?: string | null;
}

/** Sentinel for "show only the rows with NO recorded batch date" — i.e. no Ready-to-File transition in
 *  the status history. A distinct value rather than '' so it survives a round trip through a Select and
 *  cannot be confused with "no filter applied": the two mean opposite things, and conflating them would
 *  show the whole queue under a heading saying the rows have no batch date. */
export const NO_BATCH_DATE = '__none__';

/** Tolerant mapping from whatever the backend calls a status to our closed set. Keys are compared
 *  after lower-casing and stripping every non-letter, so `Awaiting_State`, `awaiting-state` and
 *  `AWAITING STATE` all land on the same entry. */
const STATUS_ALIASES: Record<string, RowFilingStatus> = {
    notready: 'notReady',
    incomplete: 'notReady',
    ready: 'ready',
    readytofile: 'ready',
    queued: 'queued',
    submitted: 'queued',
    pending: 'queued',
    awaitingstate: 'queued',
    awaitingstateapproval: 'queued',
    filed: 'filed',
    accepted: 'filed',
    complete: 'filed',
    rejected: 'rejected',
    declined: 'rejected',
    error: 'error',
    failed: 'error',
    conflict: 'error',
    manuallyfiled: 'manuallyFiled',
    filedmanually: 'manuallyFiled',
    manual: 'manuallyFiled',
};

function normalizeKey(raw: string): string {
    return raw.toLowerCase().replace(/[^a-z]/g, '');
}

/** The row's lifecycle position. A MISSING backend value falls back to the completeness verdict, which
 *  is exactly the behaviour this screen had before the lifecycle existed.
 *
 *  An UNRECOGNISED value does not: it fails closed to `notReady`, never `ready`. The API owns the
 *  status vocabulary and the frontend mirrors it BY HAND — there is no shared type, no generated
 *  client, and the only drift guard is a deep-equal assertion in the API's own test suite that this
 *  repo never sees. So the API adding a status (as it just did with `manuallyFiled`) reaches a
 *  frontend that has not been taught it yet. Falling back on `row.ready` there would take a policy the
 *  backend has just told us is IN FLIGHT and offer it as ready to file — double-filing it with a real
 *  regulator on the strength of a vocabulary mismatch. Unknown means unsafe, so it is not filable. */
export function rowFilingStatus(row: FilingLifecycleRow): RowFilingStatus {
    const raw = row.filingStatus;
    if (typeof raw === 'string' && raw.trim()) {
        const hit = STATUS_ALIASES[normalizeKey(raw)];
        return hit || 'notReady';
    }
    return row.ready ? 'ready' : 'notReady';
}

/** Whether this row may be selected and included in a filing submission. A policy that is already
 *  `queued` or `filed` must never be re-sent — that is how you double-file a policy with the state. */
export function isRowFilable(row: FilingLifecycleRow): boolean {
    return rowFilingStatus(row) === 'ready';
}

/** Whether the row has left the building (submitted or accepted).
 *
 *  ⚠️ `manuallyFiled` MUST be in this set. It means the policy is already on file with the state —
 *  staff filed it in the portal by hand — we simply have no ledger row proving we did it. It was
 *  previously reported as `queued` and so was already excluded here; splitting the label out must not
 *  quietly re-admit thousands of filed policies to the filable set and double-file them. */
export function isRowSubmitted(row: FilingLifecycleRow): boolean {
    return isSubmittedStatus(rowFilingStatus(row));
}

/** The same question asked of an already-resolved status, so a caller that has one (the review panel
 *  resolves it once per render) does not re-derive it, and the submitted-set stays defined in exactly
 *  one place. `isRowSubmitted` is this function plus the lookup.
 *
 *  This predicate also answers "is a finding a GATE or a RECORD?" for the review panel. Blockers, the
 *  Lloyd's syndicate-NAIC confirmation and the reason line are all PRE-FILING gates: they answer "may
 *  we send this?". Once a policy is on file that question is closed, so the same findings become a
 *  record of why it could not automate. Showing gate wording on a submitted row asks for an action
 *  nobody can take — it surfaced on a Manually Filed row headed "Manually filed" and stamped "Already
 *  submitted to the state" while a red band underneath insisted its blockers "must be resolved before
 *  this can be filed". `rejected` and `error` are deliberately NOT here: those filings genuinely still
 *  need fixing and re-filing. */
export function isSubmittedStatus(status: RowFilingStatus): boolean {
    return status === 'queued' || status === 'filed' || status === 'manuallyFiled';
}

/** Whether this row may be included in a PROOF BATCH — the artifact that WOULD have gone to the state,
 *  built and downloaded for inspection and never submitted.
 *
 *  Only `manuallyFiled`. These are the policies somebody filed by hand in the state portal, so there is
 *  no submission record to check the hand-filing against; building the batch we would have sent is the
 *  only way to inspect what was filed. `queued` and `filed` are excluded because their artifact is
 *  already recorded as evidence — there is nothing to reconstruct — and `ready` is excluded because
 *  its real batch file is what the existing Download batch file button already produces. */
export function isRowProofBatchEligible(row: FilingLifecycleRow): boolean {
    return rowFilingStatus(row) === 'manuallyFiled';
}

/** Whether this row may be CHECKED in the grid at all.
 *
 *  ⚠️ THIS IS NOT `isRowFilable`, AND THE TWO MUST NOT BE CONFLATED. Selection now serves two different
 *  actions: filing (filable rows) and building a proof batch for inspection (manually filed rows). This
 *  predicate is the union, so a Manually Filed row gets a checkbox and select-all works on that tab.
 *
 *  What makes that safe is that the FILING action does not read this — it reads `isRowFilable`, exactly
 *  as before, and the server refuses a manually filed policy independently (see manualFilingGuard in
 *  the internal API). A checkbox is an affordance, never a permission: widening it must never widen
 *  what can be submitted, so if you are tempted to use this in a submit payload, use `isRowFilable`. */
export function isRowInspectable(row: FilingLifecycleRow): boolean {
    return isRowFilable(row) || isRowProofBatchEligible(row);
}

/** One state's slice of a proof batch. */
export interface ProofBatchGroup<R> {
    segment: string;
    stateName: string;
    rows: R[];
    customerPolicyIds: number[];
}

export interface ProofBatchPlan<R> {
    groups: ProofBatchGroup<R>[];
    /** Rows whose state has no filing configuration — nothing can be built for them. */
    unresolved: R[];
    /** Rows dropped because they are NOT proof-batch eligible. Should always be empty in practice;
     *  a non-empty value means a caller passed rows it should not have. */
    ineligible: R[];
}

/**
 * Plan a proof-batch download: which policies get built, grouped by the state each row belongs to.
 *
 * Pure, and separated from the page for one reason — it is where the safety property of the proof
 * batch is actually decidable. Two invariants it enforces, both testable here and neither testable
 * inside a DataGrid:
 *
 *   1. IT RE-FILTERS. Only `isRowProofBatchEligible` rows survive, regardless of what the caller
 *      passed. The page already only hands it checked manually-filed rows, but a proof batch that
 *      silently included a `ready` policy would produce an artifact indistinguishable from a real
 *      filing batch, and the point of the artifact is that it is NOT one.
 *   2. IT DE-DUPES BY POLICY. Grid rows are keyed by extraction row, so one policy can be checked
 *      several times; the same collapse the filing path does, for the same reason.
 *
 * Grouped by each row's OWN state because a build is per client+state — a selection spanning states
 * must produce one file per state rather than silently building only the first.
 */
export function planProofBatch<R extends FilingLifecycleRow & { state?: string | null; customerPolicyId: number }>(
    rows: R[],
    stateSegment: (state?: string | null) => string | null,
): ProofBatchPlan<R> {
    const groups = new Map<string, ProofBatchGroup<R>>();
    const unresolved: R[] = [];
    const ineligible: R[] = [];
    const claimed = new Set<number>();

    for (const row of rows || []) {
        if (!isRowProofBatchEligible(row)) { ineligible.push(row); continue; }
        if (claimed.has(row.customerPolicyId)) continue;
        const segment = stateSegment(row.state);
        if (!segment) { unresolved.push(row); continue; }
        claimed.add(row.customerPolicyId);
        const existing = groups.get(segment);
        if (existing) {
            existing.rows.push(row);
            existing.customerPolicyIds.push(row.customerPolicyId);
        } else {
            groups.set(segment, {
                segment,
                stateName: row.state || segment,
                rows: [row],
                customerPolicyIds: [row.customerPolicyId],
            });
        }
    }
    return { groups: Array.from(groups.values()), unresolved, ineligible };
}

/** The FIVE statuses MISC (Kelly, 2026-07-29) named as the filer-facing vocabulary.
 *
 *  ⚠️ THIS IS A DISPLAY SET, NOT THE BACKEND ENUM. The backend keeps SIX (`RowFilingStatus`), because
 *  `rejected` and `error` are genuinely different facts:
 *    rejected — the state reviewed the filing and refused it. Nothing is with the state.
 *    error    — OUR transport failed. The filing may or may not have reached the state.
 *  They collapse to one LABEL because a filer's next move starts the same way for both (open the row,
 *  read the reason), but they must never collapse to one MEANING — so `statusTooltip` spells out the
 *  different next step, and the row still prints the clearinghouse's own words (`filingStatusReason`).
 *  Collapsing in the database instead would have destroyed the distinction for the reconcile job and the
 *  SOX evidence trail, which is why the enum is untouched. */
export type DisplayFilingStatus = 'notReady' | 'ready' | 'queued' | 'filed' | 'needsAttention' | 'manuallyFiled';

/** Backend status → the display set. The ONLY place the collapse happens.
 *
 *  `manuallyFiled` stays its own display status rather than folding into `filed`: both mean the policy
 *  is on file, but only one of them is something we can evidence. Merging them would put policies we
 *  have no submission record for behind a label that implies we do — the same conflation that made
 *  Pending State Approval untrustworthy in the first place. */
export function displayFilingStatus(status: RowFilingStatus): DisplayFilingStatus {
    return status === 'rejected' || status === 'error' ? 'needsAttention' : status;
}

export type LifecycleTab = 'notReady' | 'ready' | 'queued' | 'filed' | 'needsAttention' | 'manuallyFiled' | 'all';

export interface LifecycleTabDef {
    value: LifecycleTab;
    label: string;
    /** Chip colours for the count pill on the tab. */
    countBg: string;
    countFg: string;
}

/** Tab strip, in lifecycle order: before filing → in flight → done → problems → everything. */
export const LIFECYCLE_TABS: LifecycleTabDef[] = [
    { value: 'notReady', label: 'Not Ready', countBg: '#fdecea', countFg: '#c62828' },
    { value: 'ready', label: 'Ready to File', countBg: '#e6f4ea', countFg: '#1e7e34' },
    { value: 'queued', label: 'Pending State Approval', countBg: '#e8f1fb', countFg: '#1565c0' },
    { value: 'filed', label: 'Filed', countBg: '#eef2f6', countFg: '#334155' },
    { value: 'manuallyFiled', label: 'Manually Filed', countBg: '#f1edf9', countFg: '#5b3fa6' },
    { value: 'needsAttention', label: 'Needs Attention', countBg: '#fff4e0', countFg: '#b26a00' },
    { value: 'all', label: 'All', countBg: '#eef2f6', countFg: '#334155' },
];

/** Plain-language definition + WHAT ACTION IS REQUIRED, for every status and every queue tab (Kelly's
 *  checklist #3). Two rules the copy follows deliberately:
 *
 *   1. Each string says what the status MEANS and then what the filer must DO — including "nothing".
 *      "Pending State Approval" exists precisely because the old "Awaiting state" left filers unsure
 *      whether they were being asked for something, so its copy leads with "No action needed."
 *   2. `rejected` and `error` share the "Needs Attention" label but NOT the same instruction. A rejected
 *      filing is not with the state and can be fixed and re-filed; a transport error may already have
 *      landed, so re-filing blind risks a duplicate filing with a real regulator. Tooltips are where
 *      that difference is preserved. */
const STATUS_TOOLTIPS: Record<RowFilingStatus, string> = {
    notReady: 'Not Ready — the documents were read but required values are still missing or invalid '
        + '(for example the policy number, premium or a required fee). Action: open the row, fill in '
        + 'the highlighted fields, then it moves to Ready to File.',
    ready: 'Ready to File — reviewed and complete; every required value is present. Action: select the '
        + 'row and click File Policies when you are ready to submit it to the state.',
    queued: 'Pending State Approval — submitted to the state and waiting on their response. '
        + 'No action needed. It stays here, visible, until the state answers; the status then moves to '
        + 'Filed or Needs Attention on its own.',
    filed: 'Filed — the state accepted this filing and it is recorded in the Filing Queue. '
        + 'No action needed.',
    rejected: 'Needs Attention — the state reviewed this filing and refused it, so it is NOT on file. '
        + 'Action: read the reason shown on the row, correct it, then file again.',
    error: 'Needs Attention — the submission could not be completed (a connection or credential '
        + 'problem on our side). It may or may not have reached the state. Action: check the state '
        + 'portal before filing again, so the policy is not filed twice.',
    manuallyFiled: 'Manually Filed — this policy is marked as filed with the state, but it was not '
        + 'submitted through this system, so there is no submission record for it. In practice it was '
        + 'filed by hand in the state portal. No action needed, and nothing is coming back to change '
        + 'it. It can be selected for inspection only: you can build and download the batch that would '
        + 'have been sent, to check what was filed. It can never be filed from here.',
};

/** The hover text for a row's status chip. */
export function statusTooltip(status: RowFilingStatus): string {
    return STATUS_TOOLTIPS[status] || STATUS_TOOLTIPS.notReady;
}

/** The hover text for a lifecycle QUEUE tab. `needsAttention` covers both failure kinds, so its tab
 *  copy names both rather than inheriting one of them. `all` describes the queue itself. */
const TAB_TOOLTIPS: Record<LifecycleTab, string> = {
    notReady: STATUS_TOOLTIPS.notReady,
    ready: STATUS_TOOLTIPS.ready,
    queued: STATUS_TOOLTIPS.queued,
    filed: STATUS_TOOLTIPS.filed,
    manuallyFiled: STATUS_TOOLTIPS.manuallyFiled,
    needsAttention: 'Needs Attention — filings the state refused, plus submissions that failed to send. '
        + 'Action: open each row and read the reason. A refused filing can be corrected and re-filed; '
        + 'a failed send should be checked against the state portal first, in case it already landed.',
    all: 'Every transaction in this batch, whatever its status.',
};

export function tabTooltip(tab: LifecycleTab): string {
    return TAB_TOOLTIPS[tab] || '';
}

export function matchesLifecycleTab(row: FilingLifecycleRow, tab: LifecycleTab): boolean {
    if (tab === 'all') return true;
    const status = rowFilingStatus(row);
    if (tab === 'needsAttention') return status === 'rejected' || status === 'error';
    return status === tab;
}

export interface StatusChipStyle {
    label: string;
    bg: string;
    fg: string;
    /** True for the two pre-filing states, where the required-fields meter is still meaningful. */
    preFiling: boolean;
}

/** One place deciding how each lifecycle state READS to a filer, in MISC's five-status vocabulary.
 *
 *  "Pending State Approval" is deliberately never shortened to "Filed": those are different facts and
 *  conflating them is what made the old confirmation dialog lie. `rejected` and `error` share the
 *  "Needs Attention" label per Kelly's spec — see DisplayFilingStatus for why the two stay distinct
 *  underneath, and statusTooltip for the different action each one asks for. */
export function statusChipStyle(status: RowFilingStatus): StatusChipStyle {
    switch (status) {
        case 'ready':
            return { label: 'Ready to File', bg: '#e6f4ea', fg: '#1e7e34', preFiling: true };
        case 'queued':
            return { label: 'Pending State Approval', bg: '#e8f1fb', fg: '#1565c0', preFiling: false };
        case 'filed':
            return { label: 'Filed', bg: '#eef7ee', fg: '#2e7d32', preFiling: false };
        case 'manuallyFiled':
            return { label: 'Manually Filed', bg: '#f1edf9', fg: '#5b3fa6', preFiling: false };
        case 'rejected':
        case 'error':
            return { label: 'Needs Attention', bg: '#fff4e0', fg: '#b26a00', preFiling: false };
        case 'notReady':
        default:
            return { label: 'Not Ready', bg: '#fdecea', fg: '#c62828', preFiling: true };
    }
}

/** Escapes text before it is interpolated into a Swal `html` body. Server-supplied strings
 *  (clearinghouse messages, client names, block reasons) all go through this. */
export function escapeHtml(value: unknown): string {
    return String(value ?? '').replace(/[<>&"]/g, (c) => (
        { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c] as string
    ));
}

// ---------------------------------------------------------------------------
// submit-batch response contract
// ---------------------------------------------------------------------------

/** What `POST /AutomaticFiling/{state}/submit-batch` returns in `data`. Every field is optional:
 *  older builds return a thinner body and we must not crash on the difference. */
export interface SubmitBatchData {
    apiSubmitEnabled?: boolean;
    outcome?: 'filed' | 'queued' | 'rejected' | 'conflict' | 'error' | string;
    submissionNumber?: string | number | null;
    submissionStatus?: string | null;
    filed?: number[];
    queued?: number[];
    dropped?: { customerPolicyId: number; reason?: string }[];
    blocked?: { customerPolicyId: number; clientName?: string; reason?: string }[];
    messages?: string[];
    /** Build/validation failure shape (pre-existing contract, still emitted). */
    report?: { errors?: string[] };
    /** Legacy count fallback. */
    policyCount?: number;
}

/** The normalized outcome of one state's submit call, including the client-side-only `manual` and
 *  `transport` cases the backend can never report on itself. */
export type StateSubmitOutcome = 'filed' | 'queued' | 'rejected' | 'conflict' | 'error' | 'manual' | 'transport';

export interface StateSubmitResult {
    stateName: string;
    segment: string;
    /** customerPolicyIds we asked this state to file. */
    requestedIds: number[];
    outcome: StateSubmitOutcome;
    httpStatus: number | null;
    data: SubmitBatchData;
    /** The most specific human-readable reasons available for a failure. */
    errors: string[];
}

/** Vocabulary for the response's own `outcome` field, compared after the same lower-case/strip-
 *  non-letters normalization as the row statuses.
 *
 *  Deliberately SEPARATE from STATUS_ALIASES even though the words overlap: a row status has no
 *  `conflict` (it collapses to `error`), while a submit outcome does and must stay distinct. Kept
 *  exhaustive on purpose — an unrecognized verb falls through to inference, and an inference that
 *  defaults to "filed" is how a queued submission would be reported as a filing. */
const OUTCOME_ALIASES: Record<string, 'filed' | 'queued' | 'rejected' | 'conflict' | 'error'> = {
    filed: 'filed',
    accepted: 'filed',
    complete: 'filed',
    completed: 'filed',
    queued: 'queued',
    submitted: 'queued',
    pending: 'queued',
    awaiting: 'queued',
    awaitingstate: 'queued',
    awaitingapproval: 'queued',
    awaitingstateapproval: 'queued',
    awaitingreview: 'queued',
    inreview: 'queued',
    rejected: 'rejected',
    declined: 'rejected',
    refused: 'rejected',
    conflict: 'conflict',
    duplicate: 'conflict',
    alreadysubmitted: 'conflict',
    error: 'error',
    failed: 'error',
    failure: 'error',
};

/** Derive the outcome for one state's response.
 *
 *  ORDER MATTERS: the transport verdict is settled BEFORE the body's own `outcome` is trusted. A
 *  failed HTTP response that happens to carry `outcome: 'filed'` (a stale or optimistically-populated
 *  field) must never be reported as a success — reporting a filing that did not happen is the whole
 *  class of bug this screen exists to eliminate. Success therefore requires BOTH a successful response
 *  and a success outcome; a failed response can only pick WHICH kind of failure it was.
 *
 *  When the backend sends no `outcome` at all, the verdict is inferred from HTTP status and the
 *  filed/queued id arrays, so an older API still produces a truthful dialog rather than a blanket
 *  "Filed". */
export function deriveStateOutcome(
    res: { ok: boolean; status: number },
    body: { status?: boolean; message?: string } | null,
    data: SubmitBatchData,
): StateSubmitOutcome {
    if (res.status === 501 || data.apiSubmitEnabled === false) return 'manual';

    const explicit = typeof data.outcome === 'string' ? OUTCOME_ALIASES[normalizeKey(data.outcome)] : undefined;
    const httpFailed = !res.ok || body?.status === false;

    if (res.status === 409) return 'conflict';

    if (httpFailed) {
        // The request failed. The body may still say which flavour of failure it was, but it cannot
        // upgrade the result to a success.
        if (explicit === 'conflict') return 'conflict';
        if (explicit === 'error') return 'error';
        return 'rejected';
    }

    if (explicit) return explicit;

    // No explicit outcome: a non-empty `queued` with nothing filed is a queue, otherwise treat as filed.
    const queuedCount = Array.isArray(data.queued) ? data.queued.length : 0;
    const filedCount = Array.isArray(data.filed) ? data.filed.length : 0;
    if (queuedCount > 0 && filedCount === 0) return 'queued';
    return 'filed';
}

/** How many policies the state reported taking, split by kind.
 *
 *  `null` means "the backend did not report this at all" and is deliberately DIFFERENT from `0`.
 *  `filed: []` means ZERO policies were accepted — the previous code ran
 *  `filed.length || policyCount || requestedIds.length`, so an empty array fell straight through the
 *  `||` chain and the dialog announced the full requested count as filed, which is exactly the lie
 *  this branch exists to remove. A present-but-empty array must never fall back to anything. */
export function reportedCounts(data: SubmitBatchData): { filed: number | null; queued: number | null } {
    return {
        filed: Array.isArray(data.filed) ? data.filed.length : null,
        queued: Array.isArray(data.queued) ? data.queued.length : null,
    };
}

/** Total policies the state actually took (filed + queued), or `null` when it reported no counts at
 *  all and we can only go on the outcome verb. Zero is a real, reportable answer. */
export function confirmedPolicyCount(data: SubmitBatchData): number | null {
    const { filed, queued } = reportedCounts(data);
    if (filed === null && queued === null) {
        return typeof data.policyCount === 'number' ? data.policyCount : null;
    }
    return (filed ?? 0) + (queued ?? 0);
}

/** Whether a nominally-successful result actually moved any policy. A `filed`/`queued` outcome whose
 *  reported count is a hard zero (everything blocked or dropped) is NOT a success and must not let the
 *  aggregate dialog title read "Filed to the state". `null` counts fall back to trusting the verb. */
export function isEmptySuccess(result: { outcome: StateSubmitOutcome; data: SubmitBatchData }): boolean {
    if (result.outcome !== 'filed' && result.outcome !== 'queued') return false;
    return confirmedPolicyCount(result.data) === 0;
}

/** The most specific failure reasons available, in priority order: the build report's own errors, then
 *  the clearinghouse's messages, then whatever the envelope said. */
export function failureReasons(
    data: SubmitBatchData,
    body: { message?: string } | null,
    httpStatus: number | null,
): string[] {
    const reportErrors = data.report?.errors;
    if (Array.isArray(reportErrors) && reportErrors.length) return reportErrors;
    if (Array.isArray(data.messages) && data.messages.length) return data.messages;
    if (body?.message) return [body.message];
    return [httpStatus != null ? `HTTP ${httpStatus}` : 'The filing request could not be completed.'];
}

// ---------------------------------------------------------------------------
// review-panel copy: pre-filing gates vs. post-filing findings
// ---------------------------------------------------------------------------
//
// Every string a filer reads about a finding lives here rather than inline in the page, for the same
// reason the statuses do: the wording IS the product decision, it gets re-litigated, and two screens
// render it. `isSubmittedStatus` decides which voice to use; see its doc for why.

/** The pre-filing headings, exported because the single-policy Filing Review screen renders the same
 *  two bands and must not keep its own drifting copies of the words. */
export const GATE_BLOCKER_HEADING = 'Filing blockers — must be resolved before this can be filed';
export const GATE_LLOYDS_HEADING = "Lloyd's Syndicate NAIC confirmation required";

export interface BandCopy {
    heading: string;
    /** Sub-caption under the heading, or null when the heading says enough. */
    note: string | null;
}

/** Blockers band. Imperative before filing; a record of what stopped the automation after. */
export function blockerBandCopy(status: RowFilingStatus): BandCopy {
    if (!isSubmittedStatus(status)) return { heading: GATE_BLOCKER_HEADING, note: null };
    return {
        heading: 'Would have blocked automated filing',
        note: status === 'manuallyFiled'
            ? 'This policy is already on file with the state, so nothing here is outstanding for it. '
                + 'Fixing these is what lets the next policy like it file automatically.'
            : 'This policy has already been submitted, so nothing here is outstanding for it.',
    };
}

/** Lloyd's syndicate-NAIC confirmation band. `note` is appended to each flag's own reason, so it
 *  reads as one sentence. */
export function lloydsConfirmCopy(status: RowFilingStatus): BandCopy {
    return isSubmittedStatus(status)
        ? {
            heading: "Lloyd's Syndicate NAIC was never confirmed",
            note: ' — confirm the syndicate-specific NAIC-filed number so the next one can file automatically.',
        }
        : {
            heading: GATE_LLOYDS_HEADING,
            note: ' — confirm the syndicate-specific NAIC-filed number before filing.',
        };
}

export interface ReviewCounts {
    requiredReady: number;
    requiredTotal: number;
    blockers: number;
    manualReview: number;
}

/** The one-line summary under the panel heading, or null when there is nothing outstanding to say.
 *
 *  ONE builder for both voices, so the counts, the pluralisation and the "N of M" prefix cannot drift
 *  apart the way they would in two hand-written branches. Only the three labels change: before filing
 *  they are a work list, after filing they are a record. */
export function reviewReasonLine(status: RowFilingStatus, counts: ReviewCounts): string | null {
    const missing = Math.max(counts.requiredTotal - counts.requiredReady, 0);
    const submitted = isSubmittedStatus(status);
    const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

    const parts = [
        missing > 0
            ? (submitted
                ? `${missing} still missing`
                : `${missing} required ${plural(missing, 'field', 'fields')} missing`)
            : null,
        counts.blockers > 0
            ? (submitted
                ? `${counts.blockers} would have blocked automated filing`
                : `${counts.blockers} ${plural(counts.blockers, 'blocker', 'blockers')}`)
            : null,
        counts.manualReview > 0
            ? (submitted
                ? `${counts.manualReview} needing review`
                : `${counts.manualReview} ${plural(counts.manualReview, 'needs', 'need')} review`)
            : null,
    ].filter(Boolean);

    if (!parts.length) return null;
    // Required fields all present but something else is outstanding — say so, rather than leaving a
    // count that implies a missing field. A submitted row always leads with the tally, since the line
    // is a record of where the policy stood rather than a list of things to go and do.
    const prefix = (submitted || missing === 0)
        ? `${counts.requiredReady}/${counts.requiredTotal} required fields ${submitted ? 'have a value' : 'complete'} · `
        : '';
    return prefix + parts.join(' · ');
}

/** Whether the amber "Already submitted to the state" band should render.
 *
 *  The rule is narrow ON PURPOSE: `manuallyFiled` only. That is the one status whose heading and chip
 *  BOTH already say the policy is on file, so the amber band adds nothing but a third voice in warning
 *  colours — the contradiction this exists to remove.
 *
 *  `queued` and `filed` keep the band even though they are not filable either. The band's job is to
 *  make an existing submission impossible to miss, and for those two the warning is still the loudest
 *  statement of a fact worth being loud about. Narrowing it further is a separate product decision, not
 *  a side effect of fixing the manually-filed row. */
export function showAlreadySubmittedBand(status: RowFilingStatus): boolean {
    return status !== 'manuallyFiled';
}

/** The row's batch date, or null when none was recorded.
 *
 *  Tolerant of an older API build that does not send the field at all: absent and null both mean "no
 *  batch date", which is the honest answer in both cases. */
export function batchDateOf(row: FilingLifecycleRow): string | null {
    const raw = row.batchDate;
    return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

/** What the Batch Date cell should read.
 *
 *  ⚠️ THREE DIFFERENT ANSWERS, AND THEY MUST NOT COLLAPSE INTO ONE.
 *    a real date       — the day this policy was part of a batch.
 *    'Not recorded'    — no stamp. Either the policy predates the feature or nothing has evaluated it
 *                        yet. It does NOT mean "no batch happened", and the column says so rather than
 *                        rendering an em-dash that reads as an absence of activity.
 *    'Filed manually'  — a manually-filed policy: staff filed it in the state's portal, so no batch
 *                        exists at all. `markedFiledOn` (when someone marked it Filed HERE) is shown as
 *                        secondary text and explicitly labelled, because it can be long after the real
 *                        filing and must never be read as a batch date.
 *
 *  `formatDay` renders the two real dates. It is injected rather than hardcoded so this module stays
 *  free of presentation, and so the caller uses the SAME formatter as every other date column on its
 *  grid — a Batch Date reading `2026-03-08` beside a Filing Due Date reading `03-08-2026` looks like a
 *  different kind of value, which is the same one-screen disagreement the day boundary is chosen to
 *  avoid. Defaults to the raw ISO day, which is what the tests assert against.
 */
export function batchDateLabel(
    row: FilingLifecycleRow,
    formatDay: (day: string) => string = (d) => d,
): { primary: string; secondary: string | null } {
    const day = batchDateOf(row);
    if (day) return { primary: formatDay(day), secondary: null };
    if (rowFilingStatus(row) === 'manuallyFiled') {
        const marked = typeof row.markedFiledOn === 'string' && row.markedFiledOn.trim()
            ? row.markedFiledOn.trim() : null;
        return { primary: 'Filed manually', secondary: marked ? `Marked filed ${formatDay(marked)}` : null };
    }
    return { primary: 'Not recorded', secondary: null };
}
